import {
  Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Divider, Link,
  Stack, Typography,
} from '@mui/material';

import { PlacePhotoStrip } from './PlacePhotos';

/** A POI the traveller clicked on the map, awaiting a role. */
export interface PickedPoi {
  id: string;
  name: string;
  /** The key decides what the place can be used for; the label is for reading. */
  categoryKey: string;
  categoryLabel: string;
  address: string | null;
  visitable: boolean;
  avoidable: boolean;
  /** Drives the attribution line the data licences require. */
  source: 'osm' | 'manual' | 'fsq';
  phone: string | null;
  website: string | null;
  /** Our own coordinate for the place, as imported. */
  lat: number;
  lng: number;
}

/**
 * What a place clicked on the map can become.
 *
 * Shared by the single-day screen and the trip, because a place clicked on a
 * map means the same thing on both. Which lists the answer lands in is the
 * caller's business — a trip puts a stop on the day it is showing, and a
 * single day has only the one — so this asks and reports, and decides nothing.
 */
export function PlacePickerDialog({
  picked, onClose, onAssign, breakRole = null, onUseAsBreak,
}: {
  picked: PickedPoi | null;
  onClose: () => void;
  onAssign: (role: 'visit' | 'avoid') => void;
  /**
   * The break this place could serve as, when the traveller has asked for
   * that break and this is the kind of place it looks for. `null` offers no
   * such button — a café is no use as lunch, and neither is any place at all
   * if nobody asked for a lunch stop.
   */
  breakRole?: 'lunch' | 'coffee' | null;
  onUseAsBreak?: (kind: 'lunch' | 'coffee') => void;
}) {
  return (
    <Dialog open={Boolean(picked)} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ pb: 0.5 }}>{picked?.name}</DialogTitle>
      <DialogContent>
        {/*
          Shown wherever a place is, not only in the itinerary's own panel.
          Renders nothing when the enrichment cache has not reached this
          place, which is most of them.
        */}
        <PlacePhotoStrip poiId={picked?.id ?? null} height={120} />

        <Stack spacing={0.5} sx={{ pt: picked ? 1 : 0 }}>
          <Chip
            size="small"
            label={picked?.categoryLabel}
            sx={{ alignSelf: 'flex-start' }}
          />
          <Typography variant="body2" color="text.secondary">
            {picked?.address ?? 'No address recorded for this place'}
          </Typography>

          {/*
            Shown only when the source actually has them. Foursquare carries
            a phone for about three places in five and a website for one in
            two; OpenStreetMap far less often. An empty row would imply the
            place has no phone rather than that nobody recorded one.
          */}
          {picked?.phone && (
            <Typography variant="body2">
              <Link href={`tel:${picked.phone.replace(/[^\d+]/g, '')}`} underline="hover">
                {picked.phone}
              </Link>
            </Typography>
          )}
          {picked?.website && (
            <Typography variant="body2" noWrap>
              <Link href={picked.website} target="_blank" rel="noreferrer noopener" underline="hover">
                {picked.website.replace(/^https?:\/\/(www\.)?/, '').replace(/\/$/, '')}
              </Link>
            </Typography>
          )}

          {/*
            Opens Google Maps at our own coordinate for the place.

            The position we hold is the one the route was planned around, so
            sending Google the name instead would risk it resolving to a
            different branch of the same chain and quietly disagreeing with
            the map beside it. A lat/lng is unambiguous, and Google still
            identifies whatever business sits on that spot.
          */}
          {picked && (
            <Typography variant="body2" sx={{ pt: 0.5 }}>
              <Link
                href={`https://www.google.com/maps/search/?api=1&query=${picked.lat},${picked.lng}`}
                target="_blank"
                rel="noreferrer noopener"
                underline="hover"
              >
                Open in Google Maps
              </Link>
            </Typography>
          )}

          {/*
            Required, not decorative. The Foursquare data is Apache 2.0,
            which obliges attribution wherever it is used or shown — see
            data/fsq/NOTICE.txt.
          */}
          {picked?.source === 'fsq' && (
            <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
              Place data from Foursquare
            </Typography>
          )}
          {picked?.source === 'osm' && (
            <Typography variant="caption" color="text.secondary" sx={{ pt: 0.5 }}>
              Place data from OpenStreetMap
            </Typography>
          )}
        </Stack>

        <Divider sx={{ my: 2 }} />

        <Stack spacing={1}>
          {/*
            Deliberately not gated on `visitable`.

            That flag decides what the optimiser SUGGESTS — nobody wants a
            police station proposed as sightseeing. It should not override
            an explicit choice: OSM tags plenty of visitable places oddly
            (a youth club tagged amenity=police, a museum inside a civic
            building), and blocking those made the map feel broken.
          */}
          {/*
            Shown above the ordinary actions when it applies: someone who has
            asked for a lunch stop and just clicked a restaurant almost
            certainly means that, not a generic stop.
          */}
          {picked && breakRole && onUseAsBreak && (
            <Button
              variant="contained"
              color="warning"
              onClick={() => onUseAsBreak(breakRole)}
            >
              Use as the {breakRole} stop
            </Button>
          )}

          <Button
            variant={picked && breakRole ? 'outlined' : 'contained'}
            onClick={() => onAssign('visit')}
          >
            Add to places to visit
          </Button>
          <Button
            variant="outlined"
            color="warning"
            disabled={!picked?.avoidable}
            onClick={() => onAssign('avoid')}
          >
            Avoid this place
          </Button>
          {!picked?.visitable && (
            <Typography variant="caption" color="text.secondary">
              Places of this kind are not suggested automatically, but you can
              still add this one.
            </Typography>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
      </DialogActions>
    </Dialog>
  );
}
